import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react'
import BidValue from '../../../global/BidAsk/BidValue';
import AskValue from '../../../global/BidAsk/AskValue';
import { ThemeContext } from '../../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { createAlerts } from '../../../../api/request/dashboard/dashboard';
import { calculateAccountMetrics } from '../../../footer/AccountCalculation';
import { fetchAlerts } from '../../../../store/thunks/alerts';
import { GoPlus } from "react-icons/go";
import { BiMinus } from "react-icons/bi";


export default function NewAlert({ open, onClose }) {
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const [symbolId, setSymbolId] = useState("");
    const [condition, setCondition] = useState("");
    const [type, setType] = useState("");
    const [value, setValue] = useState(0);
    const [additionalSymbol, setAdditionalSymbol] = useState("")
    const [bid, setBid] = useState(null);
    const [ask, setAsk] = useState(null);
    const [digit, setDigit] = useState(null)
    const { symbols } = useSelector(state => state.watchlist)
    const { accountSummary = [] } = useSelector(state => state.accountSummary)
    const accountId = localStorage.getItem("accountID");
    const { userDetail } = useSelector(state => state.user);
    const id = userDetail?.id;
    const storedHiddenSymbols = JSON.parse(localStorage.getItem(`hiddenSymbols_${id}`)) || [];

    const filteredSymbols = useMemo(() => {
        return Object.values(symbols).filter(symbol =>
            symbol.isVisible && symbol.isUsed &&
            !storedHiddenSymbols.includes(symbol.id)
        );
    }, [symbols, storedHiddenSymbols]);

    const handleClose = () => {
        setSymbolId("");
        setCondition("");
        setType("");
        setValue(0);
        setAdditionalSymbol("");
        onClose();
    };
    const typeMap = {
        1: "alert_type_sound",
        2: "alert_type_sms",
    };

    const conditionMap = {
        1: "condition_bid_gt",
        2: "condition_bid_lt",
        3: "condition_ask_gt",
        4: "condition_ask_lt",
        5: "condition_balance_gt",
        6: "condition_balance_lt",
        7: "condition_equity_gt",
        8: "condition_equity_lt",
        9: "condition_marginlevel_gt",
        10: "condition_marginlevel_lt",
        11: "condition_pl_gt",
        12: "condition_pl_lt",
    };
    const { floatingProfitLoss, calculatedEquity, calculatedMarginLevel } = calculateAccountMetrics(accountSummary);

    const handleConditionChange = (event) => {
        const selectedCondition = Number(event.target.value);
        setCondition(selectedCondition);


        if (selectedCondition === 5 || selectedCondition === 6) {
            setValue(accountSummary?.balance || 0);
        } else if (selectedCondition === 7 || selectedCondition === 8) {
            setValue(calculatedEquity || 0);
        } else if (selectedCondition === 9 || selectedCondition === 10) {
            setValue(calculatedMarginLevel || 0);
        } else if (selectedCondition === 11 || selectedCondition === 12) {
            setValue(floatingProfitLoss || 0);
        } else {
            setValue(0);
        }
    };

    const handleTypeChange = (event) => {
        const selectedType = Number(event.target.value);
        setType(selectedType);
    };

    const handleSymbolChange = (event) => {
        const selectedSymbolId = event.target.value;

        // Allow symbol change only if condition is between 1 and 6
        if (condition >= 1 && condition <= 6) {
            setSymbolId(selectedSymbolId);
            setAdditionalSymbol(selectedSymbolId);

            const selectedSymbol = symbols[selectedSymbolId];

            if (selectedSymbol) {
                setBid(selectedSymbol.bid);
                setAsk(selectedSymbol.ask);
                setAdditionalSymbol(selectedSymbol.id);

                // Auto-set value based on condition only
                if (condition === 1) {
                    setValue(selectedSymbol.bid + 1);
                } else if (condition === 2) {
                    setValue(Number(selectedSymbol.bid - 1));
                } else if (condition === 3) {
                    setValue(selectedSymbol.ask + 1);
                } else if (condition === 4) {
                    setValue(Number(selectedSymbol.ask - 1));
                }
            } else {
                setBid(null);
                setAsk(null);
                setAdditionalSymbol("");
            }
        } else {
            toast.info("Symbol selection is only applicable for Ask/Bid conditions (1–6).");
        }
    };
    const handleSave = async () => {
        if (!accountId || !condition || !type) {
            toast.error("Please fill all fields before saving.");
            return;
        }

        let adjustedValue = value;
        let updatedSymbolId = symbolId;

        if ([5, 6, 7, 8, 9, 10, 11, 12].includes(condition)) {
            updatedSymbolId = null;
        } else if (!symbolId) {
            toast.error("Please select a symbol.");
            return;
        }

        // Now we will preserve the value entered in the text field, without overwriting it
        switch (condition) {
            case 3: // Ask >
                if (value <= ask) {
                    toast.error("Value must be greater than Ask price.");
                    return;
                }
                break;
            case 4: // Ask <
                if (value >= ask) {
                    toast.error("Value must be less than Ask price.");
                    return;
                }
                break;
            case 1: // Bid >
                if (value <= bid) {
                    toast.error("Value must be greater than Bid price.");
                    return;
                }
                break;
            case 2: // Bid <
                if (value >= bid) {
                    toast.error("Value must be less than Bid price.");
                    return;
                }
                break;
            case 5: // Balance >
                if (value <= accountSummary?.balance) {
                    toast.error("Value must be greater than balance.");
                    return;
                }
                break;
            case 6: // Balance <
                if (value >= accountSummary?.balance) {
                    toast.error("Value must be less than balance.");
                    return;
                }
                break;

            case 7: // Equity >
                if (value <= calculatedEquity) {
                    toast.error("Value must be grater than equity.");
                    return;
                }
                break;
            case 8: // Equity <
                if (value >= calculatedEquity) {
                    toast.error("Value must be less than equity.");
                    return;
                }
                break;

            case 9: // Margin Level >
                if (value <= calculatedMarginLevel) {
                    toast.error("Value must be less than margin level.");
                    return;
                }
                break;
            case 10: // Margin Level <
                if (value >= calculatedMarginLevel) {
                    toast.error("Value must be greater than margin level.");
                    return;
                }
                break;
            case 11: // P&L >
                if (value <= floatingProfitLoss) {
                    toast.error("Value must be less than P&L.");
                    return;
                }
                break;
            case 12: // P&L <
                if (value >= floatingProfitLoss) {
                    toast.error("Value must be greater than P&L.");
                    return;
                }
                break;
            default:
                adjustedValue = value;
        }


        const payload = {
            accountId: Number(accountId),
            symbolId: updatedSymbolId !== null ? Number(updatedSymbolId) : null,
            condition: Number(condition),
            type: Number(type),
            value: adjustedValue,
        };

        try {
            const response = await createAlerts(payload);

            if (response.status === 200) {
                toast.success("Alert created successfully!");
                handleClose();
                dispatch(fetchAlerts());
            }
        } catch (error) {
            console.error("Error creating alert:", error);
            toast.error("Failed to create alert.");
        }
    };

    useEffect(() => {
        if (additionalSymbol) {
            const selectedSymbol = Object.values(symbols).find(
                (symbol) => symbol.id === Number(additionalSymbol)
            );
            console.log(selectedSymbol);

            if (selectedSymbol) {

                setBid(selectedSymbol.bid);
                setAsk(selectedSymbol.ask);
                setDigit(selectedSymbol.digits)
            }
        }
    }, [symbols, additionalSymbol]);

    const StepInput = ({ value, onChange, digits }) => {
        const [inputValue, setInputValue] = useState(value.toString());

        useEffect(() => {
            if (parseFloat(inputValue) !== value) {
                setInputValue(value.toString());
            }
        }, [value]);

        const step = 1 / Math.pow(10, digits);

        const increment = () => {
            const newVal = parseFloat((value + step).toFixed(digits));
            onChange(newVal);
        };
        
        const decrement = () => {
            const newVal = parseFloat((value - step).toFixed(digits));
            if (newVal >= 0) {
                onChange(newVal);
            } else {
                onChange(0); 
            }
        };


        const handleBlur = () => {
            const parsed = parseFloat(inputValue);
            if (!isNaN(parsed)) {
                onChange(parsed);
            } else {
                setInputValue(value.toString()); // reset to last valid
            }
        };
        return (
            <Box display="flex" alignItems="center" justifyContent="space-between" border={`1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`} borderRadius={1}>
                <Button variant="text" onClick={decrement} sx={{ minWidth: '30px', height: 35 }}><BiMinus /></Button>
                {/* <TextField type="number" variant="outlined" value={value} inputProps={{ step }} onChange={(e) => onChange(e.target.value)} sx={{ input: { textAlign: 'center' }, width: 180, "& .MuiOutlinedInput-root": { height: 40, "& .MuiOutlinedInput-notchedOutline": {border: "none", }, "&:hover .MuiOutlinedInput-notchedOutline": {border: "none", }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": {border: "none", }, }, }}/> */}
                <TextField type="number" variant="outlined" sx={{ input: { textAlign: 'center' }, width: 180, "& .MuiOutlinedInput-root": { height: 40, "& .MuiOutlinedInput-notchedOutline": { border: "none", }, "&:hover .MuiOutlinedInput-notchedOutline": { border: "none", }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none", }, }, }}
                    inputProps={{
                        step,
                        onKeyDown: (e) => {
                            if (e.key === '-' || e.key === 'e') {
                                e.preventDefault();
                            }
                        },
                    }}
                    value={inputValue} onBlur={handleBlur}
                    onChange={(e) => setInputValue(e.target.value)}


                />

                <Button variant="text" onClick={increment} sx={{ minWidth: '30px', height: 40 }}><GoPlus /></Button>
            </Box>

        );
    };


    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <Box>
                    <DialogTitle sx={{ color: "white", p: 2 }} className="bg-primary">
                        <Typography id="symbol-details-modal" variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >{t('newAlert')}</Typography>
                    </DialogTitle>

                    <DialogContent sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                        <Grid container spacing={2} >
                            <Grid item xs={6} alignItems="center" justifyContent="center" display='flex' flexDirection="column">
                                <Typography variant="body1" align="center" >{t('bid')}</Typography>
                                <BidValue symbolId={additionalSymbol} digits={digit} />

                            </Grid>
                            <Grid item xs={6} alignItems="center" justifyContent="center" display='flex' flexDirection="column">
                                <Typography variant="body1" align="center">{t('ask')}</Typography>
                                <AskValue symbolId={additionalSymbol} digits={digit} />

                            </Grid>
                        </Grid>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 3 }}>

                            <FormControl fullWidth variant="outlined">
                                <InputLabel sx={{ color: darkMode ? "#ccc" : "#000" }}>{t('condition')}</InputLabel>
                                <Select label={t('condition')} value={condition} onChange={handleConditionChange} sx={{ height: 45 }}
                                    MenuProps={{ PaperProps: { sx: { paddingY: 0, '& .MuiMenuItem-root': { minHeight: '30px', paddingY: '4px', }, }, }, }}>
                                    {Object.entries(conditionMap).map(([key, value]) => (
                                        <MenuItem sx={{ fontSize: 13.5 }} key={key} value={Number(key)}>{t(value)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth variant="outlined">
                                <InputLabel sx={{ color: darkMode ? "#ccc" : "#000" }}>{t('type')}</InputLabel>
                                <Select label={t('type')} value={type} onChange={handleTypeChange} sx={{ height: 45 }}
                                    MenuProps={{ PaperProps: { sx: { paddingY: 0, '& .MuiMenuItem-root': { minHeight: '30px', paddingY: '4px', }, }, }, }}>
                                    {Object.entries(typeMap).map(([key, value]) => (
                                        <MenuItem sx={{ fontSize: 13.5 }} key={key} value={Number(key)}>{t(value)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>



                            {(condition === 1 || condition === 2 || condition === 3 || condition === 4) && (
                                <>
                                    <FormControl fullWidth variant="outlined" sx={{ mt: "18px" }}>
                                        <InputLabel>{t('symbol')}</InputLabel>
                                        <Select label={t('symbol')} value={additionalSymbol} onChange={handleSymbolChange} sx={{ height: 45 }} MenuProps={{ anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, getContentAnchorEl: null, PaperProps: { style: { maxHeight: 250, overflow: "auto" } }, }}>
                                            {filteredSymbols.map((symbol) => (
                                                <MenuItem key={symbol.id} value={symbol.id}>
                                                    {symbol.name}
                                                </MenuItem>
                                            ))}

                                        </Select>
                                    </FormControl>
                                </>
                            )}
                            {/* <TextField label={t('Value')} type="number" variant="outlined" fullWidth defaultValue={0} sx={{ "& .MuiOutlinedInput-root": { height: 45 } }} value={value} onChange={(e) => setValue(e.target.value)}
                                onKeyDown={(e) => {
                                    setValue((prev) => {
                                        let num = parseFloat(prev);
                                        let step = num >= 1000 ? 1 : num >= 1 ? 1 : 0.1;

                                        if (e.key === "ArrowUp") {
                                            return (num + step)?.toFixed(step === 0.1 ? 4 : 2);
                                        } else if (e.key === "ArrowDown") {
                                            return (num - step)?.toFixed(step === 0.1 ? 4 : 2);
                                        }
                                        return num;
                                    });
                                }} /> */}
                            <FormControl fullWidth>
                                <Typography variant="caption">{t('Value')}</Typography>
                                <StepInput value={value} onChange={setValue} digits={digit} />
                            </FormControl>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ padding: 2, backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                        <Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
                            <Button onClick={handleClose} variant="outlined" color="primary">{t('cancel')}</Button>
                            <Button onClick={handleSave} variant="contained" color="primary" sx={{ ml: 2 }}>{t('save')}</Button>
                        </Box>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    )
}
